import { useMutation, useQuery } from '@apollo/client/react';
import { Alert, Box, Button, HStack, Text, VStack } from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { ChatComposerRow } from '#src/components/chat-composer-row';
import {
  MessageList,
  type ChatMessageQueryOptions,
} from '#src/components/message-list';
import { toaster } from '#src/components/ui/toaster';
import { CHAT_MESSAGE_QUERY_PROCESSOR_ID } from '#src/constants/processors';
import { CREATE_MESSAGE } from '#src/graphql/mutations/create-message';
import { EXECUTE_PROCESSOR } from '#src/graphql/mutations/execute-processor';
import { GET_MESSAGES } from '#src/graphql/queries/messages';
import { useThreadFocusSearchParams } from '#src/hooks/use-thread-focus-search-params';
import {
  usePageFooter,
  usePageLayout,
} from '#src/providers/page-layout-provider';
import { useCurrentUser } from '#src/providers/user-provider';
import { resolveFocusParamToRootId } from '#src/utils/message-thread-focus';

export function Dashboard() {
  const { footerRef } = usePageFooter();
  const { setFooterMode } = usePageLayout();
  const user = useCurrentUser();

  const {
    rawFocusMessageId,
    expandThread,
    collapseThread,
    replaceStripFocusParam,
  } = useThreadFocusSearchParams();

  useEffect(() => {
    setFooterMode('fixed');
    return () => setFooterMode('float');
  }, [setFooterMode]);
  const [messageText, setMessageText] = useState('');

  const [createMessage, { loading: isSendingMessage }] =
    useMutation(CREATE_MESSAGE);
  const [executeChatMessageQuery] = useMutation(EXECUTE_PROCESSOR);

  const [chatMessageQueryLoadingId, setChatMessageQueryLoadingId] = useState<
    string | null
  >(null);

  const {
    data: messagesData,
    loading: isLoadingMessages,
    error: messagesError,
    refetch: refetchMessages,
  } = useQuery(GET_MESSAGES, {
    fetchPolicy: 'cache-and-network',
  });

  const sortedMessages = useMemo(
    () =>
      [...(messagesData?.messages || [])].sort(
        (a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      ),
    [messagesData?.messages],
  );

  const focusedRootId = useMemo(
    () => resolveFocusParamToRootId(rawFocusMessageId, sortedMessages),
    [rawFocusMessageId, sortedMessages],
  );

  const displayMessages = useMemo(
    () =>
      focusedRootId
        ? sortedMessages.filter((m) => m.id === focusedRootId)
        : sortedMessages,
    [sortedMessages, focusedRootId],
  );

  useEffect(() => {
    if (isLoadingMessages) return;
    if (rawFocusMessageId && !focusedRootId) {
      replaceStripFocusParam();
    }
  }, [
    isLoadingMessages,
    rawFocusMessageId,
    focusedRootId,
    replaceStripFocusParam,
  ]);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [displayMessages]);

  const handleRunChatMessageQuery = useCallback(
    async (
      messageId: string,
      query: string,
      options?: ChatMessageQueryOptions,
    ) => {
      setChatMessageQueryLoadingId(messageId);
      try {
        const variables: Record<string, unknown> = { messageId, query };
        if (options?.iterative) {
          variables.iterative = true;
        }
        const { data } = await executeChatMessageQuery({
          variables: {
            input: {
              processorId: CHAT_MESSAGE_QUERY_PROCESSOR_ID,
              variables,
            },
          },
        });
        const ep = data?.executeProcessor;
        const inner = ep?.result as
          | {
              success?: boolean;
              error?: string;
              documentUpdated?: boolean;
            }
          | undefined;
        if (!ep?.success) {
          throw new Error(ep?.error ?? 'Processor failed');
        }
        if (inner?.success === false) {
          throw new Error(inner.error ?? 'Request failed');
        }
        await refetchMessages();
        if (inner?.documentUpdated === true) {
          toaster.create({
            title: 'Document updated',
            description:
              'Changes were saved and a reply was added to this thread.',
            type: 'success',
          });
        } else {
          toaster.create({
            title: 'AI note added',
            description: 'A reply was added to this thread.',
            type: 'success',
          });
        }
      } catch (error) {
        toaster.create({
          title: 'Could not run AI action',
          description:
            error instanceof Error
              ? error.message
              : 'An unexpected error occurred',
          type: 'error',
        });
        throw error;
      } finally {
        setChatMessageQueryLoadingId(null);
      }
    },
    [executeChatMessageQuery, refetchMessages],
  );

  const handleSendMessage = async () => {
    const value = messageText.trim();
    if (!value) return;
    try {
      await createMessage({
        variables: {
          input: {
            value,
            parentMessageId: focusedRootId ?? undefined,
          },
        },
      });
      setMessageText('');
      await refetchMessages();
      toaster.create({
        title: 'Message sent',
        description: 'Your message was created successfully',
        type: 'success',
      });
    } catch (error) {
      toaster.create({
        title: 'Failed to send message',
        description:
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred',
        type: 'error',
      });
    }
  };

  const footerContent = user.email ? (
    <VStack gap={2} align="stretch" width="100%">
      {focusedRootId && (
        <HStack gap={2} align="center" fontSize="xs" color="fg.muted">
          <Text>Replying in thread</Text>
          <Button size="xs" variant="ghost" onClick={() => collapseThread()}>
            Cancel
          </Button>
        </HStack>
      )}
      <ChatComposerRow
        value={messageText}
        onChange={setMessageText}
        onSubmit={handleSendMessage}
        placeholder={focusedRootId ? 'Type a reply…' : 'Type a message…'}
        loading={isSendingMessage}
      />
    </VStack>
  ) : null;

  return (
    <>
      {/* Render message input into footer portal */}
      {footerRef.current && createPortal(footerContent, footerRef.current)}

      <Box
        display="flex"
        flexDirection="column"
        height="100%"
        minHeight={0}
        bg="var(--background-base-color)"
      >
        <HStack
          flex={1}
          minHeight={0}
          align="stretch"
          gap={4}
          overflow="hidden"
        >
          <Box flex={1} minHeight={0} display="flex" flexDirection="column">
            {!user.email ? (
              <Alert.Root status="info">
                <Alert.Indicator />
                <Alert.Title>
                  Please set your user email in Settings to view messages.
                </Alert.Title>
              </Alert.Root>
            ) : (
              <MessageList
                messages={displayMessages}
                loading={isLoadingMessages}
                error={messagesError ?? undefined}
                emptyMessage="No messages found."
                focusedRootId={focusedRootId}
                onExpandThread={expandThread}
                messagesEndRef={messagesEndRef}
                onRunChatMessageQuery={handleRunChatMessageQuery}
                chatMessageQueryLoadingId={chatMessageQueryLoadingId}
                alignMessagesToBottom
                onMessageDeleted={() => void refetchMessages()}
              />
            )}
          </Box>
        </HStack>
      </Box>
    </>
  );
}
